// frontend/src/pages/RegisterPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import logo from '../assets/constellia.png';
import { AuthAlert } from '../components/AuthAlert';
import { getErrorMessage } from '../utils/errorUtils';
import './RegisterPage.css';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { t, language, setLanguage, availableLanguages } = useI18n();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    propertyName: ''
  });

  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // 1. Auto-detect browser language on mount if not already set
  useEffect(() => {
    const browserLang = (navigator.language || navigator.userLanguage || 'en').toLowerCase();

    let detectedLang = 'en';
    if (browserLang.startsWith('fr')) {
      detectedLang = 'fr';
    } else if (browserLang.startsWith('sw')) {
      detectedLang = 'sw';
    }

    // Set language in global i18n context
    setLanguage(detectedLang);
  }, [setLanguage]);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleLanguageChange = (e) => {
    const chosenLang = e.target.value;
    setLanguage(chosenLang);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(
        '/auth/register',
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          propertyName: formData.propertyName,
          language: language
        },
        { withCredentials: true }
      );

      setUser(response.data.user);

      // Redirect straight to site onboarding wizard or dashboard
      navigate('/farms/new');
    } catch (err) {
      setError(getErrorMessage(err, t, 'auth'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <img src={logo} alt="Constellia Logo" className="auth-logo" />
          <h2>{t('auth.register')}</h2>
          <p>{t('app.subtitle')}</p>
        </div>

        {/* Quebec Law / Accessibility Compliant Language Selector */}
        <div className="language-selector-wrapper">
          <label htmlFor="register-language-select">
            🌐 {t('nav.language')}:
          </label>
          <select
            id="register-language-select"
            value={language}
            onChange={handleLanguageChange}
            className="language-select-input"
          >
            <option value="fr">Français</option>
            <option value="en">English</option>
            <option value="sw">Kiswahili</option>
          </select>
        </div>

        {/*{error && <div className="auth-error-alert">{error}</div>}*/}
        <AuthAlert error={error} />

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label>{t('auth.propertyName')}</label>
            <input
              type="text"
              name="propertyName"
              value={formData.propertyName}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('auth.firstName')}</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('auth.lastName')}</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>{t('auth.email')}</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>{t('auth.password')}</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>{t('auth.confirmPassword')}</label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
            />
          </div>

          <button
            type="submit"
            className="btn-primary-lg full-width"
            disabled={submitting}
          >
            {submitting ? t('common.loading') : t('auth.registerButton')}
          </button>
        </form>

        <div className="auth-footer">
          <span>{t('auth.haveAccount')} </span>
          <Link to="/login">{t('auth.login')}</Link>
        </div>
      </div>
    </div>
  );
}
